import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Database, PenLine, Plus, Target, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { API, errorText } from '../governance_v2/constants';
import { MODULE_LABELS } from '../iad/constants';
import { VERDICT, fmtDate } from './constants';
import { PredictionFields, cleanPrediction } from './ProjectFormDialog';

const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('es', { maximumFractionDigits: 2 }));

const Bars = ({ p }) => {
  const vals = [p.baseline_value, p.expected_value, p.actual_value].filter((v) => v != null).map(Math.abs);
  const max = Math.max(...vals, 1);
  const Row = ({ label, v, cls }) => (
    <div className="grid grid-cols-[90px_1fr_70px] items-center gap-2 text-xs">
      <span className="text-slate-500">{label}</span>
      <div className="h-2.5 rounded bg-slate-100 dark:bg-slate-800">{v != null && <div className={`h-2.5 rounded ${cls} transition-[width] duration-500`} style={{ width: `${(Math.abs(v) / max) * 100}%` }} />}</div>
      <span className="text-right font-mono">{fmt(v)}</span>
    </div>
  );
  return (
    <div className="space-y-1.5">
      <Row label="Línea base" v={p.baseline_value} cls="bg-slate-400" />
      <Row label="Predicción" v={p.expected_value} cls="bg-amber-500" />
      <Row label="Realidad" v={p.actual_value} cls={p.verdict === 'cumplida' ? 'bg-emerald-500' : p.verdict === 'no_cumplida' ? 'bg-red-500' : 'bg-sky-500'} />
    </div>
  );
};

const PredictionCard = ({ p, companyId, projectId, canManage, onChanged }) => {
  const [actual, setActual] = useState('');
  const record = useMutation({
    mutationFn: () => axios.put(`${API}/companies/${companyId}/projects/${projectId}/predictions/${p.id}/actual`, { actual_value: Number(actual) }),
    onSuccess: () => { setActual(''); toast.success('Valor real registrado'); onChanged(); },
    onError: (e) => toast.error(errorText(e)),
  });
  const del = useMutation({
    mutationFn: () => axios.delete(`${API}/companies/${companyId}/projects/${projectId}/predictions/${p.id}`),
    onSuccess: () => { toast.success('Predicción eliminada'); onChanged(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700" data-testid={`prediction-card-${p.id}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Target className="h-4 w-4 text-amber-500" />
        <p className="font-semibold text-slate-900 dark:text-white">{p.kpi_name}</p>
        {p.module_type && <Badge variant="outline">{MODULE_LABELS[p.module_type] || p.module_type}</Badge>}
        <Badge className={VERDICT[p.verdict]?.cls} data-testid={`prediction-verdict-${p.id}`}>{VERDICT[p.verdict]?.label}</Badge>
        {canManage && <button type="button" onClick={() => window.confirm('¿Eliminar esta predicción?') && del.mutate()} className="ml-auto text-slate-400 hover:text-red-500" data-testid={`prediction-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
      <Bars p={p} />
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>Fecha esperada: {fmtDate(p.expected_date)}</span>
        {p.achievement_pct != null && <span data-testid={`prediction-achievement-${p.id}`}>Logro: <b>{p.achievement_pct}%</b></span>}
        {p.deviation_pct != null && <span>Desviación vs predicción: <b>{p.deviation_pct > 0 ? '+' : ''}{p.deviation_pct}%</b></span>}
        {p.actual_source && <span className="inline-flex items-center gap-1">{p.actual_source === 'manual' ? <PenLine className="h-3 w-3" /> : <Database className="h-3 w-3" />}
          {p.actual_source === 'manual' ? 'Captura manual' : 'Detectado en ingesta'} · {fmtDate(p.measured_at)}</span>}
      </div>
      {canManage && (
        <div className="mt-3 flex gap-2">
          <Input type="number" value={actual} onChange={(e) => setActual(e.target.value)} placeholder="Capturar valor real manualmente" className="max-w-xs" data-testid={`prediction-actual-input-${p.id}`} />
          <Button size="sm" variant="outline" disabled={actual === '' || record.isPending} onClick={() => record.mutate()} data-testid={`prediction-actual-save-${p.id}`}>Registrar</Button>
        </div>
      )}
    </div>
  );
};

export const PredictionPanel = ({ project, companyId, canManage }) => {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);
  const onChanged = () => qc.invalidateQueries({ queryKey: ['project', companyId, project.id] });
  const add = useMutation({
    mutationFn: () => axios.post(`${API}/companies/${companyId}/projects/${project.id}/predictions`, cleanPrediction(draft)),
    onSuccess: () => { setDraft(null); toast.success('Predicción agregada'); onChanged(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="space-y-3" data-testid="prediction-panel">
      <p className="text-sm text-slate-500">Compara lo que predijo el agente con el valor real del KPI tras la ejecución. El valor real se detecta solo en las ingestas posteriores al inicio del proyecto; la captura manual tiene prioridad.</p>
      {project.predictions.map((p) => <PredictionCard key={p.id} p={p} companyId={companyId} projectId={project.id} canManage={canManage} onChanged={onChanged} />)}
      {!project.predictions.length && <p className="text-sm text-slate-400" data-testid="prediction-empty">Este proyecto aún no tiene predicciones.</p>}
      {canManage && (draft ? (
        <div className="space-y-2 rounded-xl border border-dashed border-amber-400/60 p-4">
          <PredictionFields companyId={companyId} idx="new" value={draft} onChange={setDraft} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancelar</Button>
            <Button size="sm" disabled={!draft.kpi_name.trim() || draft.expected_value === '' || add.isPending} onClick={() => add.mutate()} data-testid="prediction-add-save">Guardar predicción</Button>
          </div>
        </div>
      ) : <Button size="sm" variant="outline" onClick={() => setDraft({ kpi_name: '', module_type: '', expected_value: '', expected_date: '' })} data-testid="prediction-add-btn"><Plus className="mr-1 h-4 w-4" />Agregar predicción</Button>)}
    </div>
  );
};
